import React, { useEffect } from 'react';
import MainContainer from '../components/layout/MainContainer';
import Skeleton from '../components/CreateMoldOrder/Skeleton';
import ErrorPage from '../components/pages/Error';
import { MoldOrderFirstLevelType } from '../types/mold';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { useAppState } from '../context/AppStateContext';
import { useMold } from '../hooks/useMold';
import MoldStatus from '../components/shared/MoldStatus';
import { getDynamicIcon } from '../utils/icon';

interface CreateMoldOrderMenuContainerProps { }

interface MoldOrderMainTypeButtonProps {
    type: MoldOrderFirstLevelType;
    onClick: (type: MoldOrderFirstLevelType) => void;
    disabled?: boolean;
    disabledReason?: string;
}

// Color style definitions - extracted as constant to avoid recreation
const COLOR_STYLES: Record<string, any> = {
    emerald: {
        gradient: 'from-emerald-500 to-teal-600',
        hoverGradient: 'hover:from-emerald-600 hover:to-teal-700',
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        hoverIconBg: 'group-hover:bg-emerald-200',
        border: 'border-emerald-200',
        hoverBorder: 'hover:border-emerald-300',
        shadow: 'shadow-emerald-200/50',
        hoverShadow: 'hover:shadow-emerald-300/60'
    },
    blue: {
        gradient: 'from-blue-500 to-indigo-600',
        hoverGradient: 'hover:from-blue-600 hover:to-indigo-700',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        hoverIconBg: 'group-hover:bg-blue-200',
        border: 'border-blue-200',
        hoverBorder: 'hover:border-blue-300',
        shadow: 'shadow-blue-200/50',
        hoverShadow: 'hover:shadow-blue-300/60'
    },
    amber: {
        gradient: 'from-amber-500 to-orange-600',
        hoverGradient: 'hover:from-amber-600 hover:to-orange-700',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        hoverIconBg: 'group-hover:bg-amber-200',
        border: 'border-amber-200',
        hoverBorder: 'hover:border-amber-300',
        shadow: 'shadow-amber-200/50',
        hoverShadow: 'hover:shadow-amber-300/60'
    },
    yellow: {
        gradient: 'from-amber-500 to-orange-600',
        hoverGradient: 'hover:from-amber-600 hover:to-orange-700',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        hoverIconBg: 'group-hover:bg-amber-200',
        border: 'border-amber-200',
        hoverBorder: 'hover:border-amber-300',
        shadow: 'shadow-amber-200/50',
        hoverShadow: 'hover:shadow-amber-300/60'
    },
    green: {
        gradient: 'from-emerald-500 to-teal-600',
        hoverGradient: 'hover:from-emerald-600 hover:to-teal-700',
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        hoverIconBg: 'group-hover:bg-emerald-200',
        border: 'border-emerald-200',
        hoverBorder: 'hover:border-emerald-300',
        shadow: 'shadow-emerald-200/50',
        hoverShadow: 'hover:shadow-emerald-300/60'
    },
    purple: {
        gradient: 'from-purple-500 to-violet-600',
        hoverGradient: 'hover:from-purple-600 hover:to-violet-700',
        iconBg: 'bg-purple-100',
        iconColor: 'text-purple-600',
        hoverIconBg: 'group-hover:bg-purple-200',
        border: 'border-purple-200',
        hoverBorder: 'hover:border-purple-300',
        shadow: 'shadow-purple-200/50',
        hoverShadow: 'hover:shadow-purple-300/60'
    },
    red: {
        gradient: 'from-red-500 to-red-600',
        hoverGradient: 'hover:from-red-600 hover:to-red-700',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        hoverIconBg: 'group-hover:bg-red-200',
        border: 'border-red-200',
        hoverBorder: 'hover:border-red-300',
        shadow: 'shadow-red-200/50',
        hoverShadow: 'hover:shadow-red-300/60'
    },
    gray: {
        gradient: 'from-gray-500 to-gray-600',
        hoverGradient: 'hover:from-gray-600 hover:to-gray-700',
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-600',
        hoverIconBg: 'group-hover:bg-gray-200',
        border: 'border-gray-200',
        hoverBorder: 'hover:border-gray-300',
        shadow: 'shadow-gray-200/50',
        hoverShadow: 'hover:shadow-gray-300/60'
    }
};

const MoldOrderMainTypeButton: React.FC<MoldOrderMainTypeButtonProps> = ({
    type,
    onClick,
    disabled = false,
    disabledReason
}) => {
    const { getMoldOrderMainTypeLabel, getHeaderStyleConfig } = useAppState();
    
    // Get configuration from AppStateContext
    const config = getHeaderStyleConfig(type);
    const IconComponent = config.uiConfig?.icon 
        ? getDynamicIcon(config.uiConfig.icon) as React.ComponentType<{ className?: string; size?: number; strokeWidth?: number; color?: string }>
        : null;

    const styles = COLOR_STYLES[config.colorCode] || COLOR_STYLES.gray;

    return (
        <div className="relative group">
            <button
                className={`relative w-full max-w-sm mx-auto h-auto aspect-[3/2] rounded-2xl 
                           overflow-hidden transition-all duration-300 transform 
                           ${!disabled ? 'hover:scale-[1.02] hover:-translate-y-1 cursor-pointer' : 'cursor-not-allowed'} 
                           ${!disabled ? styles.hoverShadow : ''}
                           bg-gradient-to-br ${styles.gradient} ${!disabled ? styles.hoverGradient : ''}
                           shadow-lg ${styles.shadow} border ${styles.border} ${!disabled ? styles.hoverBorder : ''}
                           `}
                onClick={() => !disabled && onClick(type)}
                disabled={disabled}
                data-cy={`create-order-type-${type}`}
                title={disabled && disabledReason ? disabledReason : getMoldOrderMainTypeLabel(type)}
            >
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-30"></div>
                
                {/* Disabled Overlay */}
                {disabled && (
                    <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[1px] z-10">
                        <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
                            <div className="bg-white/90 rounded-full p-2 shadow-lg">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            {disabledReason && (
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/95 rounded-lg px-3 py-2 shadow-lg text-right max-w-[200px]">
                                    <p className="text-xs text-gray-700 font-medium leading-tight whitespace-pre-line">{disabledReason}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

            {/* Content Container */}
            <div className="relative h-full flex flex-col items-center justify-center px-6 py-5 text-white">
                {/* Icon Container */}
                <div className={`flex items-center justify-center w-16 h-16 sm:w-18 sm:h-18 
                               rounded-xl ${styles.iconBg} ${!disabled ? styles.hoverIconBg : ''} 
                               transition-all duration-300 mb-4 shadow-md`}>
                    <div className={`${styles.iconColor} transition-colors duration-300`}>
                        {IconComponent ? <IconComponent className="w-16 h-16" /> : null}
                    </div>
                </div>

                {/* Text Content */}
                <div className="flex flex-col items-center text-center">
                    <span className="font-medium text-xs sm:text-sm mb-1 text-white/90">
                        สร้างใบงาน
                    </span>
                    <span className="font-bold text-base sm:text-lg leading-tight">
                        {getMoldOrderMainTypeLabel(type)}
                    </span>
                </div>
            </div>

            {/* Hover Effect Overlay */}
            <div className={`absolute inset-0 bg-gradient-to-t from-black/10 to-transparent 
                           ${!disabled ? 'opacity-0 group-hover:opacity-100' : 'opacity-0'} transition-opacity duration-300`}></div>
            
            {/* Available Badge */}
            {!disabled && (
                <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>พร้อมใช้งาน</span>
                </div>
            )}
        </button>
    </div>
    )
}

const CreateMoldOrderMenuContainer: React.FC<CreateMoldOrderMenuContainerProps> = () => {
    const navigate = useNavigate();
    const {
        mold: selectedMold,
        loading: moldLoading,
        error,
        fetchMold
    } = useMold();
    const [searchParams] = useSearchParams();
    const selectedParam = searchParams.get('selected');

    const { featureToggles, getMoldStatusLabel } = useAppState();
    
    // Handle setting the mold order main type
    const handleSetMoldOrderMainType = (type: MoldOrderFirstLevelType) => {
        const params = new URLSearchParams();
        params.set('type', type);
        if (selectedParam) params.set('selected', selectedParam);
        // Forward the copy-flow params (parentOrderId, copyMode, subType) so a copy
        // intent survives this container hop — they were previously dropped, which
        // silently disabled the copy flow through the menu.
        const parentOrderId = searchParams.get('parentOrderId');
        if (parentOrderId) params.set('parentOrderId', parentOrderId);
        const copyMode = searchParams.get('copyMode');
        if (copyMode) params.set('copyMode', copyMode);
        const subType = searchParams.get('subType');
        if (subType) params.set('subType', subType);
        navigate(`${ROUTES.CREATE_MOLD_ORDERS}?${params.toString()}`);
    };

    // Helper to generate disabled reason message
    const getDisabledReason = (customMessage?: string) => {
        if (!selectedMold || customMessage) return customMessage;
        return `ไม่สามารถสร้างใบงานได้\nเนื่องจากแม่พิมพ์อยู่ในสถานะ ${getMoldStatusLabel(selectedMold)}`;
    };

    // Get mold order configurations from app state
    const { getMoldOrderCreationConfig, moldOrderCreation } = useAppState();
    
    // Order types to display (in order) - from client configuration
    const orderTypes = moldOrderCreation.orderTypes as MoldOrderFirstLevelType[];

    // Build button configurations from client configuration
    const buttonConfigs = orderTypes.map(type => {
        const orderConfig = getMoldOrderCreationConfig(type);
        const creationConfig = orderConfig?.creationConfig;

        // Default behavior if no creationConfig is defined
        const allowedStatuses = creationConfig?.allowedStatuses || [];
        const featureToggle = creationConfig?.featureToggle;
        const customDisabledMessage = creationConfig?.customDisabledMessage;

        const isStatusAllowed = !selectedMold || allowedStatuses.length === 0 || allowedStatuses.includes(selectedMold.status);
        const isFeatureEnabled = !featureToggle || featureToggles[featureToggle];
        const disabled = !isFeatureEnabled || !isStatusAllowed;

        let disabledReason: string | undefined;
        if (!isFeatureEnabled && customDisabledMessage) {
            disabledReason = customDisabledMessage;
        } else if (!isStatusAllowed) {
            disabledReason = getDisabledReason();
        }

        return {
            type,
            disabled,
            disabledReason,
        };
    });

    useEffect(() => {
        if (selectedParam) {
            fetchMold(String(selectedParam));
        }
        // If no selectedParam, do nothing (no mold context needed)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedParam]);

    if (moldLoading) {
        return (
            <MainContainer>
                <Skeleton />
            </MainContainer>
        );
    }
    if (error) return <ErrorPage />;

    return (
        <MainContainer>
            {/* Background with subtle gradient */}
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
                <div className="flex items-center justify-center px-4 py-12">
                    <div className="w-full max-w-6xl mx-auto">
                        {/* Header Section */}
                        <div className="text-center mb-6">
                            {/* Main Title */}
                            <div className="mb-8">
                                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-3">
                                    สร้างใบงานใหม่
                                </h1>
                                {selectedMold && <div className='mb-12 flex flex-col sm:flex-row gap-2 items-center justify-center text-slate-600 text-md md:text-lg font-medium max-w-2xl mx-auto leading-relaxed'>
                                    <span>
                                        สำหรับแม่พิมพ์{' '}
                                        <a
                                            href={`/molds/${selectedMold.id}`}
                                            className="text-blue-600 hover:underline hover:text-blue-800 transition-colors duration-150"
                                            onClick={e => {
                                                e.preventDefault();
                                                navigate(`/molds/${selectedMold.id}`);
                                            }}
                                        >
                                            {selectedMold.sbiId}
                                        </a>
                                    </span>
                                    <MoldStatus mold={selectedMold} />
                                </div>}
                                <div className="w-48 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto rounded-full"></div>
                            </div>
                            {/* Subtitle */}
                            <p className="text-slate-700 text-lg md:text-xl font-medium max-w-2xl mx-auto mb-0">
                                เลือกประเภทของใบงานที่ต้องการสร้าง
                            </p>
                        </div>
                        <div className="flex justify-center">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 w-full max-w-7xl">
                                {buttonConfigs.map((config) => (
                                    <MoldOrderMainTypeButton
                                        key={config.type}
                                        onClick={handleSetMoldOrderMainType}
                                        type={config.type}
                                        disabled={config.disabled}
                                        disabledReason={config.disabledReason}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Quick Tips Section */}
                        <div className="mt-16 text-center">
                            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/50 max-w-4xl mx-auto">
                                <h3 className="text-xl font-semibold text-slate-700 mb-6">💡 เคล็ดลับการใช้งาน</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                                    <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-100">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-white text-sm font-bold">1</span>
                                            </div>
                                            <div className="text-left">
                                                <h4 className="font-semibold text-slate-700 mb-1">เลือกประเภทใบงาน</h4>
                                                <p className="text-slate-600">คลิกที่ประเภทใบงานที่ต้องการสร้างตามวัตถุประสงค์การใช้งาน</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-100">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-white text-sm font-bold">2</span>
                                            </div>
                                            <div className="text-left">
                                                <h4 className="font-semibold text-slate-700 mb-1">เลือกแม่พิมพ์</h4>
                                                <p className="text-slate-600">ระบุแม่พิมพ์ที่ต้องการดำเนินการพร้อมรายละเอียดที่จำเป็น</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-100">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-white text-sm font-bold">3</span>
                                            </div>
                                            <div className="text-left">
                                                <h4 className="font-semibold text-slate-700 mb-1">กรอกรายละเอียด</h4>
                                                <p className="text-slate-600">ระบุข้อมูลที่จำเป็นเช่น วันที่กำหนดเสร็จ ผู้รับผิดชอบ และหมายเหตุ</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-100">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-white text-sm font-bold">4</span>
                                            </div>
                                            <div className="text-left">
                                                <h4 className="font-semibold text-slate-700 mb-1">ตรวจสอบและส่ง</h4>
                                                <p className="text-slate-600">ตรวจสอบข้อมูลให้ถูกต้องครบถ้วนก่อนสร้างใบงานในระบบ</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainContainer>
    );
};

export default CreateMoldOrderMenuContainer;
